import React from 'react';

export interface PaginationBarProps {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const PaginationBar: React.FC<PaginationBarProps> = ({
  page,
  pageSize,
  totalCount,
  totalPages,
  onPageChange,
}) => {
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, totalCount);

  const pages = buildPageNumbers(page, totalPages);

  return (
    <nav
      className="flex items-center justify-between px-1 py-3"
      aria-label="Pagination"
    >
      {/* Range summary */}
      <p className="text-sm text-gray-600 select-none">
        {totalCount === 0 ? (
          'No results'
        ) : (
          <>
            Showing <span className="font-medium">{rangeStart}</span>–
            <span className="font-medium">{rangeEnd}</span> of{' '}
            <span className="font-medium">{totalCount}</span>
          </>
        )}
      </p>

      {/* Page buttons */}
      <div className="flex items-center gap-1">
        <PageButton
          label="Previous"
          icon="←"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          aria-label="Go to previous page"
        />

        {pages.map((p, i) =>
          p === '…' ? (
            <span
              key={`ellipsis-${i}`}
              className="w-9 text-center text-sm text-gray-400 select-none"
              aria-hidden
            >
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p as number)}
              disabled={p === page}
              aria-label={`Page ${p}`}
              aria-current={p === page ? 'page' : undefined}
              className={`w-9 h-9 rounded text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                p === page
                  ? 'bg-blue-600 text-white cursor-default'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              {p}
            </button>
          ),
        )}

        <PageButton
          label="Next"
          icon="→"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          aria-label="Go to next page"
        />
      </div>
    </nav>
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface PageButtonProps {
  label: string;
  icon: string;
  onClick: () => void;
  disabled: boolean;
  'aria-label': string;
}

const PageButton: React.FC<PageButtonProps> = ({ label, icon, onClick, disabled, 'aria-label': ariaLabel }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={ariaLabel}
    className={`flex items-center gap-1 px-3 h-9 rounded text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
      disabled
        ? 'text-gray-300 cursor-not-allowed'
        : 'text-gray-700 hover:bg-gray-100'
    }`}
  >
    <span aria-hidden>{icon}</span>
    <span>{label}</span>
  </button>
);

/** Produces a window like: 1 … 4 5 6 … 12 */
function buildPageNumbers(current: number, total: number): (number | '…')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const result: (number | '…')[] = [1];

  if (current > 3) result.push('…');

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  for (let p = start; p <= end; p++) result.push(p);

  if (current < total - 2) result.push('…');

  result.push(total);
  return result;
}

export default PaginationBar;
